import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  RemoveCircle as RemoveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { IBaseUserDataPlanViewModel, IPlanViewModel, IUserViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { useAuth } from '../../../context';
import { TypeUser } from '../../../types/enums';

interface ModalPlanUsersProps {
  open: boolean;
  onClose: () => void;
  plan: IPlanViewModel | null;
  loading?: boolean;
  onRemoveUser?: (userId: string, planId: string) => Promise<void>;
}

interface PlanStatistics {
  totalUsers: number;
  totalRevenue: number;
  averageRevenuePerUser: number;
  usersByState: { [key: string]: number };
  usersByType: { [key: string]: number };
}

export function ModalPlanUsers({ open, onClose, plan, loading = false, onRemoveUser }: ModalPlanUsersProps) {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<IBaseUserDataPlanViewModel[]>([]);
  const [statistics, setStatistics] = useState<PlanStatistics | null>(null);
  const [removingUser, setRemovingUser] = useState<string | null>(null);
  const [confirmRemoveUser, setConfirmRemoveUser] = useState<{ userId: string; userName: string } | null>(null);

  // Verifica se o usuário logado é ADM
  const isAdmin = user?.type === TypeUser.ADM;

  useEffect(() => {
    if (plan && plan.users) {
      setFilteredUsers(plan.users);
      calculateStatistics(plan);
    }
  }, [plan]);

  useEffect(() => {
    if (plan && plan.users) {
      const filtered = plan.users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.address?.uf?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, plan]);

  const calculateStatistics = (planData: IPlanViewModel) => {
    const users = planData.users;
    const totalUsers = users?.length || 0;
    const totalRevenue = totalUsers * planData.price;
    const averageRevenuePerUser = totalUsers > 0 ? totalRevenue / totalUsers : 0;

    // Estatísticas por estado
    const usersByState: { [key: string]: number } = {};
    users?.forEach(user => {
      const state = user.address?.uf || 'Não informado';
      usersByState[state] = (usersByState[state] || 0) + 1;
    });

    // Estatísticas por tipo de usuário
    const usersByType: { [key: string]: number } = {};
    users?.forEach(user => {
      const type = user.type ? getTypeUserLabel(user.type) : 'Não informado';
      usersByType[type] = (usersByType[type] || 0) + 1;
    });



    setStatistics({
      totalUsers,
      totalRevenue,
      averageRevenuePerUser,
      usersByState,
      usersByType,

    });
  };

  const getTypeUserLabel = (type: number) => {
    const typeLabels = {
      0: 'Usuário Base',
      1: 'Empresa',
      2: 'Usuário',
      3: 'Colaborador',
      4: 'Administrador',
    };
    return typeLabels[type as keyof typeof typeLabels] || 'Desconhecido';
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const handleRemoveUser = async (userId: string) => {
    if (!plan || !onRemoveUser) return;

    try {
      setRemovingUser(userId);
      await onRemoveUser(userId, plan.id);

      // Atualiza a lista local removendo o usuário
      const updatedUsers = filteredUsers.filter(user => user.id !== userId);
      setFilteredUsers(updatedUsers);

      // Recalcula as estatísticas
      if (plan) {
        const updatedPlan = { ...plan, users: updatedUsers };
        calculateStatistics(updatedPlan);
      }

      // Fecha o modal de confirmação
      setConfirmRemoveUser(null);
    } catch (error) {
      console.error('Erro ao remover usuário:', error);
    } finally {
      setRemovingUser(null);
    }
  };

  const handleConfirmRemove = (userId: string, userName: string) => {
    setConfirmRemoveUser({ userId, userName });
  };

  const handleCancelRemove = () => {
    setConfirmRemoveUser(null);
  };

  if (!plan) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          maxHeight: '90vh',
          margin: { xs: '16px', sm: '32px' },
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PeopleIcon fontSize="small" color="primary" />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }} color={theme.colors.secondary}>
              Usuários do Plano: {plan.name}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Estatísticas do Plano */}
            {statistics && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.colors.secondary }}>
                  Estatísticas do Plano
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <PeopleIcon color="primary" />
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                            Total de Usuários
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                          {statistics.totalUsers}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', backgroundColor: 'rgba(76, 175, 80, 0.08)' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <MoneyIcon color="success" />
                          <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 600 }}>
                            Receita Total
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                          {formatPrice(statistics.totalRevenue)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <TrendingUpIcon color="warning" />
                          <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 600 }}>
                            Receita Média/Usuário
                          </Typography>
                        </Box>
                        <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                          {formatPrice(statistics.averageRevenuePerUser)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Estatísticas Adicionais */}
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} sm={6}>
                    <Card sx={{ backgroundColor: 'rgba(0, 150, 136, 0.08)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Distribuição por Estado
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {Object.entries(statistics.usersByState).map(([state, count]) => (
                            <Chip
                              key={state}
                              label={`${state}: ${count}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card sx={{ backgroundColor: 'rgba(103, 58, 183, 0.08)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Distribuição por Tipo
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {Object.entries(statistics.usersByType).map(([type, count]) => (
                            <Chip
                              key={type}
                              label={`${type}: ${count}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Barra de Pesquisa */}
            <TextField
              fullWidth
              placeholder="Pesquisar usuários por nome, email, cidade ou estado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />

            {/* Lista de Usuários */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Usuários ({filteredUsers.length})
              </Typography>
              {!isAdmin && onRemoveUser && (
                <Tooltip title="Apenas administradores podem remover usuários dos planos">
                  <Alert severity="info" sx={{ maxWidth: '400px' }}>
                    <Typography variant="caption">
                      <strong>Nota:</strong> Apenas administradores podem remover usuários dos planos
                    </Typography>
                  </Alert>
                </Tooltip>
              )}
            </Box>

            {filteredUsers.length === 0 ? (
              <Box textAlign="center" p={4}>
                <Typography color="text.secondary">
                  {searchTerm ? 'Nenhum usuário encontrado com os critérios de busca' : 'Nenhum usuário cadastrado neste plano'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredUsers.map((user) => (
                  <Grid item xs={12} sm={6} md={4} key={user.id}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Avatar
                            src={user.profilePictureId}
                            sx={{ width: 48, height: 48 }}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {user.name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={user.type ? getTypeUserLabel(user.type) : 'Usuário'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                          {onRemoveUser && isAdmin && (
                            <Tooltip title="Remover usuário do plano">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleConfirmRemove(user.id, user.name)}
                                  disabled={loading || removingUser === user.id}
                                  sx={{
                                    opacity: removingUser === user.id ? 0.5 : 1,
                                  }}
                                >
                                  {removingUser === user.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <RemoveIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>

                        <Box display="flex" flexDirection="column" gap={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>

                          {user.address && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {user.address.city}, {user.address.uf}
                              </Typography>
                            </Box>
                          )}

                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Expira em: {formatDate(user.userPlan?.expirationDate)}
                            </Typography>
                          </Box>
                        </Box>

                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            minWidth: '120px',
            borderRadius: '8px',
          }}
        >
          Fechar
        </Button>
      </DialogActions>

      {/* Modal de Confirmação de Remoção */}
      <Dialog
        open={!!confirmRemoveUser}
        onClose={handleCancelRemove}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }} color="error">
            Confirmar Remoção
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" color="text.primary" mb={2}>
            Tem certeza que deseja remover <strong>{confirmRemoveUser?.userName}</strong> do plano <strong>{plan.name}</strong>?
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> Esta ação irá remover o usuário do plano atual.
              O usuário perderá acesso às funcionalidades do plano.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancelRemove}
            variant="outlined"
            disabled={removingUser === confirmRemoveUser?.userId}
            sx={{ borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => confirmRemoveUser && handleRemoveUser(confirmRemoveUser.userId)}
            color="error"
            variant="contained"
            disabled={removingUser === confirmRemoveUser?.userId}
            startIcon={removingUser === confirmRemoveUser?.userId ? <CircularProgress size={16} /> : <RemoveIcon />}
            sx={{ borderRadius: '8px' }}
          >
            {removingUser === confirmRemoveUser?.userId ? 'Removendo...' : 'Confirmar Remoção'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
